import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface BillingRow {
  readonly billingId: string;
  readonly billingCode: string;
  readonly saasTenantName: string | null;
  readonly saasTenantCode: string | null;
  readonly billingType: string;
  readonly chargeType: string;
  readonly status: string;
  readonly currency: string;
  readonly netAmount: string;
}

const BILLING_TYPES = ['MONTHLY', 'QUARTERLY', 'ANNUAL', 'CONSUMPTION', 'CONTRACT', 'ONE_TIME', 'RECURRING'];
const BILLING_STATUSES = ['DRAFT', 'CALCULATED', 'INVOICED', 'FAILED', 'CANCELLED'];
const PAGE = 25;

/**
 * M44 §9 Billing Dashboard (§28). Commercial operators run the billing engine
 * for a subscription and browse billing transactions. Viewing needs
 * M44_BILLING_VIEW; generating a bill / marking invoiced needs
 * M44_BILLING_EXECUTE (SoD).
 */
export function BillingDashboard(): ReactNode {
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<BillingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [subscriptionId, setSubscriptionId] = useState('');
  const [billingType, setBillingType] = useState('MONTHLY');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('billingType', typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (search.trim()) params.set('search', search.trim());
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: BillingRow[]; total: number }>(`/v1/billing/bills?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, typeFilter, statusFilter, search, page, reload]);

  const generate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!subscriptionId.trim()) { setActionError('A subscription id is required.'); return; }
    try {
      await apiRequest('/v1/billing/bills/generate', {
        method: 'POST',
        body: { subscriptionId: subscriptionId.trim(), billingType },
        token,
      });
      setNotice('Bill generated.');
      setSubscriptionId('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised to execute billing (M44_BILLING_EXECUTE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const markInvoiced = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/billing/bills/${id}/mark-invoiced`, { method: 'POST', body: {}, token });
      setNotice('Bill marked invoiced.');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised for this action (M44_BILLING_EXECUTE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="billing-dashboard">
      <h1>Billing Dashboard</h1>

      <section aria-label="billing-generate">
        <h2>Generate Bill</h2>
        <form aria-label="billing-form" onSubmit={(e) => void generate(e)}>
          <RecordPicker<{ subscriptionId: string; subscriptionCode: string }>
            endpoint="/v1/billing/subscriptions?pageSize=500"
            idField="subscriptionId"
            label="Subscription"
            testId="field-subscription-id"
            value={subscriptionId}
            optionLabel={(s) => s.subscriptionCode}
            onPick={setSubscriptionId}
            emptyText="No subscriptions yet"
          />
          <label>Type
            <select aria-label="field-billing-type" value={billingType} onChange={(e) => setBillingType(e.target.value)}>
              {BILLING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-billing">Generate</button>
        </form>
        {actionError && <p role="alert" aria-label="billing-action-error">{actionError}</p>}
        {notice && <p aria-label="billing-notice">{notice}</p>}
      </section>

      <section aria-label="billing-filters">
        <label>Type
          <select aria-label="filter-type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {BILLING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {BILLING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Search<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="billing-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="billing-total">{total} billing transaction(s).</p>
          <table aria-label="billing-table">
            <thead>
              <tr><th scope="col">Code</th><th scope="col">Customer</th><th scope="col">Type</th><th scope="col">Charge</th><th scope="col">Net</th><th scope="col">Status</th><th scope="col">Actions</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7}>No billing transactions.</td></tr>
              ) : rows.map((b) => (
                <tr key={b.billingId} data-billing={b.billingId}>
                  <td aria-label={`billing-code-${b.billingId}`}>{b.billingCode}</td>
                  <td>{personLabel(b.saasTenantName, b.saasTenantCode) ?? '—'}</td>
                  <td>{b.billingType}</td>
                  <td>{b.chargeType}</td>
                  <td>{b.currency} {money(Number(b.netAmount))}</td>
                  <td aria-label={`billing-status-${b.billingId}`}>{b.status}</td>
                  <td>
                    <button type="button" aria-label={`mark-invoiced-${b.billingId}`} onClick={() => void markInvoiced(b.billingId)}>Mark Invoiced</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="billing-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
